import * as Sentry from '@sentry/react'

function getEnvironment(): 'prd' | 'stg' | 'dev' {
  // Vite MODE is set by build:prod, build:stg scripts
  if (import.meta.env.MODE === 'prd') return 'prd'
  if (import.meta.env.MODE === 'stg') return 'stg'
  return 'dev'
}

/**
 * Get the DSN from environment variables
 */
function getDsn(): string | undefined {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  // Don't initialize Sentry in development if no DSN is provided
  if (!dsn) {
    console.log('Sentry DSN not provided')
    return undefined
  }

  return dsn
}

/**
 * Custom error filter to exclude common development errors and noise
 */
function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  const environment = getEnvironment()
  // Filter out development-only errors
  if (environment === 'dev') {
    // Skip React DevTools extension errors
    if (event.exception?.values?.[0]?.value?.includes('__REACT_DEVTOOLS_')) {
      return null
    }

    // Skip extension-related errors
    if (
      event.exception?.values?.[0]?.value?.includes(
        'Extension context invalidated',
      )
    ) {
      return null
    }

    // Skip ResizeObserver loop limit exceeded (common browser noise)
    if (
      event.exception?.values?.[0]?.value?.includes(
        'ResizeObserver loop limit exceeded',
      )
    ) {
      return null
    }
  }

  // Filter out network errors that are not actionable
  if (event.exception?.values?.[0]?.type === 'NetworkError') {
    return null
  }

  // Filter out script loading errors from browser extensions
  if (event.exception?.values?.[0]?.value?.includes('Loading CSS chunk')) {
    return null
  }

  // Filter out typing-related errors that might be too noisy
  if (event.exception?.values?.[0]?.value?.includes('Typing module error')) {
    return null
  }

  return event
}

/**
 * Initialize Sentry with appropriate configuration for the current environment
 */
export function initializeSentry(): void {
  const dsn = getDsn()

  if (!dsn) {
    console.log('Sentry initialization skipped - no DSN provided')
    return
  }

  // Get replay configuration from environment variables
  const replaySessionSampleRate = parseFloat(
    import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE ?? '0',
  )
  const replayOnErrorSampleRate = parseFloat(
    import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE ?? '1.0',
  )

  // Allow complete disabling of replay in development

  const environment = getEnvironment()

  try {
    Sentry.init({
      dsn,
      environment,

      // Performance monitoring
      tracesSampleRate: environment === 'prd' ? 0.1 : 1.0, // Lower sample rate in production

      // Session replay configuration (cost optimization)
      // Use environment variables to control replay sampling
      replaysSessionSampleRate: replaySessionSampleRate,
      replaysOnErrorSampleRate: replayOnErrorSampleRate,

      // Error filtering
      beforeSend,

      // Integration configuration
      integrations: [
        // Browser tracing for performance monitoring
        Sentry.browserTracingIntegration({
          // Capture interactions like clicks, form submissions
          enableInp: true,

          // Track navigation timing
          enableLongTask: true,

        }),

        // Session replay with privacy settings
        Sentry.replayIntegration({
          // Don't mask text content for typing app - we want to see actual content
          maskAllText: false,

          // Block media elements from recording to save bandwidth
          blockAllMedia: true,

          // Only mask password inputs and sensitive data
          maskAllInputs: false,

          // Mask specific input types and sensitive elements
          mask: [
            'input[type="password"]', 
            'input[type="email"]',
            '.user-data',
            '[data-sensitive]'
          ],

          // Additional privacy settings for production
          ...(environment === 'prd' && {
            // Block specific selectors from recording if needed
            block: ['.sensitive-data', '[data-sensitive]', '.user-personal-info'],

            // Mask additional elements with personal data
            mask: [
              'input[type="password"]',
              'input[type="email"]',
              '.user-data',
              '[data-personal]',
              '.license-info',
            ],

            // Ignore specific DOM mutations
            ignoreClass: ['sentry-ignore'],
          }),
        }),

        // Context lines for better debugging
        Sentry.contextLinesIntegration(),

        // Breadcrumbs for better error context
        Sentry.breadcrumbsIntegration({
          console: environment !== 'prd', // Only log console breadcrumbs in non-production
          dom: {
            serializeAttribute:
              environment === 'prd' ? [] : ['id', 'class', 'aria-label'],
          },
          fetch: true,
          history: true,
          sentry: true,
          xhr: true,
        }),
      ],

      // Release information
      release: import.meta.env.VITE_BUILD_NUMBER || new Date().toISOString(),

      // Debug mode for development
      debug: environment === 'dev',

      // Additional options for production
      ...(environment === 'prd' && {
        // Send default PII (Personally Identifiable Information)
        sendDefaultPii: false,

        // Attach stack trace to messages
        attachStacktrace: true,

        // Maximum number of breadcrumbs
        maxBreadcrumbs: 50,
      }),
    })

    // Set additional context
    const buildNumber =
      import.meta.env.VITE_BUILD_NUMBER || `deploy-${Date.now()}`

    Sentry.setContext('app', {
      name: 'digitane-typing',
      version: buildNumber,
      environment,
      build: buildNumber,
    })

    // Set user context if available
    const userAgent = navigator.userAgent
    const language = navigator.language

    Sentry.setContext('device', {
      userAgent,
      language,
      screenResolution: `${screen.width}x${screen.height}`,
      colorDepth: screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })

    // Set tags for better filtering
    Sentry.setTag('app', 'digitane-typing')
    Sentry.setTag('environment', environment)

    console.log(`Sentry initialized for ${environment} environment`)
  } catch (error) {
    console.error('Failed to initialize Sentry:', error)
  }
}

/**
 * Utility function to capture exceptions manually
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext('additional', context)
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

/**
 * Utility function to capture messages manually
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
): void {
  Sentry.captureMessage(message, level)
}

/**
 * Utility function to add breadcrumb manually
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  Sentry.addBreadcrumb(breadcrumb)
}

/**
 * Utility function to set user context
 */
export function setUser(user: Sentry.User): void {
  Sentry.setUser(user)
}

/**
 * Utility function to set tag
 */
export function setTag(key: string, value: string): void {
  Sentry.setTag(key, value)
}

/**
 * Error boundary component using Sentry
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary

/**
 * Performance measurement utilities
 */
export const withSentryProfiling = Sentry.withProfiler

/**
 * Export Sentry for advanced usage
 */
export { Sentry }

/**
 * Typing-specific utilities for better error context
 */
export function captureTypingError(
  error: Error, 
  typingContext: {
    currentWord?: string;
    gradeLevel?: string;
    course?: string;
    stage?: string;
  }
): void {
  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'typing')
    scope.setContext('typing', typingContext)
    Sentry.captureException(error)
  })
}

/**
 * Capture user progress events for analytics
 */
export function captureTypingProgress(
  eventName: string,
  data: {
    course?: string;
    grade?: string;
    score?: number;
    accuracy?: number;
    wpm?: number;
  }
): void {
  addBreadcrumb({
    message: eventName,
    category: 'typing.progress',
    data,
    level: 'info',
  })
}